import {
  createBrowserRouter,
  Navigate,
} from 'react-router';
import { RootLayout, Wrapper } from './App.component';
import { LoadingOverlay } from '../common/loadingOverlay/LoadingOverlay.component';
import { initI18n } from '../common/i18next/i18n';
import { getLocalizationLangCd } from '../common/util/i18n';

async function publicLoader() {
  const publicUrl = import.meta.env?.VITE_PUBLIC_URL ?? '';
  const cacheBuster = window.CACHE_BUSTER ?? '';
  const globalsConfig = await fetch(
    `${publicUrl}/globalsConfig.json?b=${cacheBuster}`,
    { cache: 'no-cache', headers: { Accept: 'application/json' } },
  ).then((r) => r.json());
  return { globalsConfig };
}

async function rootLoader() {
  const csrfText = await fetch('/webcast/webapi/csrfToken/token').then((r) => r.text());
  let token = null;
  try {
    token = JSON.parse(csrfText)?.token;
  } catch (e) {
    if (import.meta.env.MODE === 'development') {
      window.location.href = '/webcast/login';
    } else {
      window.location.reload();
    }
  }
  const cacheBuster = window.CACHE_BUSTER ?? '';
  const publicUrl = import.meta.env?.VITE_PUBLIC_URL ?? '';
  const headers = { 'X-ON24-CSRF-TOKEN': token };

  const [adminData, globalsConfig] = await Promise.all([
    fetch('/webcast/webapi/adminInfo/adminProperties', { headers })
      .then((r) => r.text())
      .then((t) => JSON.parse(t)),
    fetch(`${publicUrl}/globalsConfig.json?b=${cacheBuster}`, {
      cache: 'no-cache',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json()),
  ]);

  const language = adminData?.adminProperties?.adminLanguage
    || getLocalizationLangCd(true, globalsConfig.supportedLanguages);

  await initI18n({
    loadPath: '/locales/{{lng}}/translations.json',
    responseParser: (data) => {
      const dictionary = JSON.parse(data);
      const {
        common, update, registration, managereg, create, overview, table,
        webcasts, archive, eventadmin, accountdashboard, manageusers, smartText, pageBuilder, ...restDictionary
      } = dictionary;
      return Object.assign(common, update, registration, managereg, create, overview, table,
        webcasts, archive, eventadmin, accountdashboard, manageusers, smartText, pageBuilder,
        { ...restDictionary });
    },
    language,
  });

  return { token, adminData, globalsConfig };
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        loader: publicLoader,
        hydrateFallbackElement: <LoadingOverlay />,
        children: [
          { path: '/webcast/login', lazy: () => import('../pages/login/LoginPage.component').then((m) => ({ Component: m.default })) },
          { path: '/webcast/logout', lazy: () => import('../pages/login/LoginPage.component').then((m) => ({ Component: m.default })) },
          { path: '/webcast/resetpassword', lazy: () => import('../pages/resetPassword/ResetPasswordPage.component').then((m) => ({ Component: m.default })) },
        ],
      },
      {
        id: 'root',
        path: '/webcast',
        loader: rootLoader,
        shouldRevalidate: () => false,
        element: <Wrapper />,
        hydrateFallbackElement: <LoadingOverlay />,
        children: [
          { path: '', element: <Navigate to="/webcast/webcasts" /> },
          { path: 'sso/webcasts', element: <Navigate to="/webcast/webcasts" /> },
          { path: 'profilesettings', lazy: () => import('../pages/profileSettings/ProfileSettingsRoute').then((m) => ({ Component: m.default })) },
          { path: 'webcasts', lazy: () => import('../pages/events/EventsPage.component').then((m) => ({ Component: m.default })) },
          { path: 'home', lazy: () => import('../pages/home/HomePage.component').then((m) => ({ Component: m.default })) },
          { path: 'speakerlibrary', lazy: () => import('../pages/speakerLibrary/SpeakerLibrary.component').then((m) => ({ Component: m.default })) },
          { path: 'create', lazy: () => import('../pages/create/CreateWebcastPage.component').then((m) => ({ Component: m.default })) },
          { path: 'create/:eventId', lazy: () => import('../pages/create/CreateWebcastPage.component').then((m) => ({ Component: m.default })) },
          { path: 'update/:eventId', lazy: () => import('../pages/overview/OverviewPage.component').then((m) => ({ Component: m.default })) },
          { path: 'registration/:eventId', lazy: () => import('../pages/pageBuilder/RegBuilderRoute').then((m) => ({ Component: m.default })) },
          { path: 'series/create', lazy: () => import('../pages/pageBuilder/CreateEventSeriesRoute').then((m) => ({ Component: m.default })) },
          { path: 'series/:groupId', lazy: () => import('../pages/pageBuilder/EditEventSeriesRoute').then((m) => ({ Component: m.default })) },
          { path: 'groupregistration/:eventId', lazy: () => import('../pages/groupRegistration/GroupRegistrationRoute').then((m) => ({ Component: m.default })) },
          { path: 'managereg/:eventId', lazy: () => import('../pages/managereg/ManageRegPage.component').then((m) => ({ Component: m.default })) },
          { path: 'archive/:eventId', lazy: () => import('../pages/archive/ArchivePage.component').then((m) => ({ Component: m.default })) },
          { path: 'html5console/:eventId', lazy: () => import('../pages/console/ConsolePage.component').then((m) => ({ Component: m.default })) },
          { path: 'eventadmin/:eventId', lazy: () => import('../pages/eventadmin/EventAdminPage.component').then((m) => ({ Component: m.default })) },
          { path: 'aicontent/:eventId', lazy: () => import('../pages/aicontent/AIContentPage.component').then((m) => ({ Component: m.default })) },
          { path: 'captionlist/:eventId', lazy: () => import('../pages/captionlist/CaptionListPage.component').then((m) => ({ Component: m.default })) },
          { path: 'accountdashboard', lazy: () => import('../pages/accountdashboard/AccountDashboardPage.component').then((m) => ({ Component: m.default })) },
          { path: 'manageusers', lazy: () => import('../pages/manageusers/ManageUsersPage.component').then((m) => ({ Component: m.default })) },
        ],
      },
    ],
  },
]);
